import React, { useEffect, useState } from "react";
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Container } from "semantic-ui-react";
import { UserService } from "../../services/userService";
import { validateOptionalSecondaryEmail } from "../../utils/validation";

export const UserProfileEditPage: React.FC = () => {
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [secondaryEmail, setSecondaryEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [messagePreference, setMessagePreference] = useState("email");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [updateStatus, setUpdateStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [passwordUpdateStatus, setPasswordUpdateStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [isOpen, setIsOpen] = useState(true);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [optInOpen, setOptInOpen] = useState(false); // Popup open
  const [optInSelection, setOptInSelection] = useState<boolean | null>(null); // true=opt-in, false=opt-out
  const [hasOptedIn, setHasOptedIn] = useState<boolean | null>(null);
  const [givenNameError, setGivenNameError] = useState("");
  const [familyNameError, setFamilyNameError] = useState("");
  const [secondaryEmailError, setSecondaryEmailError] = useState("");

  const [streetError, setStreetError] = useState("");
  const [cityError, setCityError] = useState("");
  const [stateError, setStateError] = useState("");
  const [zipError, setZipError] = useState("");

  const [optInSubmitStatus, setOptInSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [prevMessagePreference, setPrevMessagePreference] = useState<string>("email");
  const [showOptInStatus, setShowOptInStatus] = useState(false);

  const normalizePhoneNumber = (phone: string) => phone.replace(/\D/g, ""); // remove non-digits

  const validatePhoneNumber = (phone: string) => {
    // Reject if invalid characters
    if (!/^[\d\s\-\(\)]+$/.test(phone)) {
      return false;
    }
    // Check normalized length for 10 digits
    const normalized = normalizePhoneNumber(phone);
    if (normalized.length !== 10) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (window.location.hash === "#password") {
      setIsPasswordOpen(true);
    }

    (async () => {
      try {
        const data = await UserService.getUserProfile();
        if (data) {
          setGivenName(data.givenName || "");
          setFamilyName(data.familyName || "");
          setEmail(data.email || "");
          setSecondaryEmail(data.secondaryEmail || "");
          setCompanyName(data.companyName || "");
          setPhoneNumber(data.phoneNumber || "");
          setMessagePreference(data.messagePreference || "email");
          setStreet(data.street || "");
          setCity(data.city || "");
          setState(data.state || "");
          setZip(data.zip || "");
          setHasOptedIn(data.hasOptedIn ?? false);
        }
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    setShowOptInStatus(false);
    const timer = setTimeout(() => {
      setShowOptInStatus(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, [hasOptedIn]);

  useEffect(() => {
    if (!optInOpen) {
      // Modal closed, start delay to show opt-in status
      setShowOptInStatus(false);
      const timer = setTimeout(() => {
        setShowOptInStatus(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // When modal is open, hide opt-in status
      setShowOptInStatus(false);
    }
  }, [optInOpen]);

  // Trigger popup when user selects SMS or Both
  const handleMessagePreferenceChange = async (value: string) => {
    setPrevMessagePreference(messagePreference);
    setMessagePreference(value);

    if (value === "sms" || value === "both") {
      setOptInOpen(true);
    } else if (value === "email") {
      setOptInOpen(false);
      setOptInSelection(null);
      setHasOptedIn(false);
      try {
        await UserService.saveMessageOptIn({ optedIn: false }); // persist opt-out
      } catch (error) {
        console.error("Failed to save opt-out status:", error);
      }
    } else {
      setOptInOpen(false);
      setOptInSelection(false);
      setHasOptedIn(false);
    }
  };

  const handleOptInCancel = () => {
    setOptInOpen(false);
    // revert messagePreference to previous if current is sms/both
    if ((messagePreference === "sms" || messagePreference === "both") && prevMessagePreference !== messagePreference) {
      setMessagePreference(prevMessagePreference);
    }
  };

  const handleOptInConfirm = async () => {
    if (optInSelection === null) {
      return;
    }
    try {
      await UserService.saveMessageOptIn({ optedIn: optInSelection ?? false });
      setHasOptedIn(optInSelection);
      setOptInSubmitStatus("success");

      // Save updated messagePreference immediately
      await UserService.updateUserProfile({
        messagePreference,
        phoneNumber,
      });

      setTimeout(() => {
        setOptInSubmitStatus("idle");
        setOptInOpen(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to save opt-in status:", error);
      setOptInSubmitStatus("error");
      setTimeout(() => setOptInSubmitStatus("idle"), 3000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setGivenNameError("");
    setFamilyNameError("");
    setSecondaryEmailError("");
    setPhoneNumberError("");
    setStreetError("");
    setCityError("");
    setStateError("");
    setZipError("");

    if (
      !/^[a-zA-Z\s\-']+$/.test(givenName) ||
      !givenName.trim() ||
      givenName.length > 50
    ) {
      setGivenNameError(
        "Given name can only contain letters, spaces, hyphens, and apostrophes, max 50 characters."
      );
      return;
    }

    if (
      !/^[a-zA-Z\s\-']+$/.test(familyName) ||
      !familyName.trim() ||
      familyName.length > 50
    ) {
      setFamilyNameError(
        "Family name can only contain letters, spaces, hyphens, and apostrophes, max 50 characters."
      );
      return;
    }

    if (!validateOptionalSecondaryEmail(secondaryEmail, email)) {
      setSecondaryEmailError(
        "Enter a valid secondary email that is different from the primary email."
      );
      return;
    }

    if (!/^[\d\s\-\(\)]+$/.test(phoneNumber)) {
      setPhoneNumberError(
        "Phone number can only contain numbers, spaces, parentheses, or hyphens."
      );
      return;
    }

    const normalizedPhone = normalizePhoneNumber(phoneNumber);
    if (normalizedPhone.length !== 10) {
      setPhoneNumberError("Phone number must contain exactly 10 digits.");
      return;
    }

    if (!street.trim() || street.length > 100) {
      setStreetError("Street address is required and cannot exceed 100 characters.");
      return;
    }

    if (!/^[a-zA-Z\s\-']+$/.test(city) || !city.trim() || city.length > 50) {
      setCityError(
        "City can only contain letters, spaces, hyphens, and apostrophes, max 50 characters."
      );
      return;
    }

    if (!/^[a-zA-Z\s\.\-']+$/.test(state) || !state.trim() || state.length > 100) {
      setStateError(
        "State/Place can only contain letters, spaces, dots, hyphens, apostrophes, max 100 characters."
      );
      return;
    }

    if (!/^\d{5}(-\d{4})?$/.test(zip)) {
      setZipError("Zip must be a 5-digit code or 5+4 digit code (e.g., 12345 or 12345-6789).");
      return;
    }

    setPhoneNumberError("");

    try {
      await UserService.updateUserProfile({
        givenName,
        familyName,
        companyName,
        secondaryEmail: secondaryEmail.trim(),
        phoneNumber,
        messagePreference,
        street,
        city,
        state,
        zip,
      });
      setUpdateStatus("success");
      setTimeout(() => setUpdateStatus("idle"), 3000);
    } catch (error) {
      console.error("Failed to update user:", error);
      setUpdateStatus("error");
      setTimeout(() => setUpdateStatus("idle"), 3000);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setPasswordUpdateStatus("error");
      setTimeout(() => setPasswordUpdateStatus("idle"), 3000);
      return;
    }

    if (newPassword.length < 8) {
      setPasswordUpdateStatus("error");
      setTimeout(() => setPasswordUpdateStatus("idle"), 3000);
      return;
    }

    try {
      await UserService.updatePassword({ currentPassword, newPassword });
      setPasswordUpdateStatus("success");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => setPasswordUpdateStatus("idle"), 3000);
    } catch (error) {
      console.error("Failed to update password:", error);
      setPasswordUpdateStatus("error");
      setTimeout(() => setPasswordUpdateStatus("idle"), 3000);
    }
  };

  if (loading) return <Typography>Loading...</Typography>;

  const profilePage = () => {
    window.location.href = "/profile";
  };

  return (
    <Container
      style={{
        minHeight: "75vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div style={{ marginTop: "3rem", marginBottom: "3rem", width: "100%", maxWidth: 600 }}>
        <Typography variant="h3">Edit Profile</Typography>

        {/* Profile Details Accordion */}
        <Accordion expanded={isOpen} onChange={() => setIsOpen(!isOpen)} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            Edit Profile Details
          </AccordionSummary>
          <AccordionDetails>
            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              <TextField
                label="Given Name"
                type="text"
                value={givenName}
                required
                onChange={(e) => {
                  setGivenName(e.target.value);
                  if (givenNameError) setGivenNameError("");
                }}
                fullWidth
                margin="normal"
                helperText={givenNameError}
                error={givenNameError !== ""}
              />
              <TextField
                label="Family Name"
                type="text"
                value={familyName}
                required
                onChange={(e) => {
                  setFamilyName(e.target.value);
                  if (familyNameError) setFamilyNameError("");
                }}
                fullWidth
                margin="normal"
                helperText={familyNameError}
                error={familyNameError !== ""}
              />
              <TextField
                label="Company Name"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                id="primary-email"
                label="Primary Email"
                type="email"
                value={email}
                fullWidth
                margin="normal"
                disabled={true}
                helperText="Primary email cannot be changed here."
              />
              <TextField
                id="secondary-email"
                label="Secondary Email (CC notifications only)"
                type="email"
                value={secondaryEmail}
                onChange={(e) => {
                  setSecondaryEmail(e.target.value);
                  if (secondaryEmailError) setSecondaryEmailError("");
                }}
                fullWidth
                margin="normal"
                error={secondaryEmailError !== ""}
                helperText={
                  secondaryEmailError ||
                  "Receives notification copies only and cannot be used to sign in."
                }
              />
              <TextField
                label="Phone Number"
                type="text"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  if (phoneNumberError) setPhoneNumberError("");
                }}
                fullWidth
                margin="normal"
                error={phoneNumberError !== ""}
                helperText={phoneNumberError}
              />
              <TextField
                label="Street"
                type="text"
                value={street}
                onChange={(e) => {
                  setStreet(e.target.value);
                  if (streetError) setStreetError("");
                }}
                fullWidth
                margin="normal"
                required
                helperText={streetError}
                error={streetError !== ""}
              />

              <TextField
                label="City"
                type="text"
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  if (cityError) setCityError("");
                }}
                fullWidth
                margin="normal"
                required
                helperText={cityError}
                error={cityError !== ""}
              />

              <TextField
                label="State"
                type="text"
                value={state}
                onChange={(e) => {
                  setState(e.target.value);
                  if (stateError) setStateError("");
                }}
                fullWidth
                margin="normal"
                required
                helperText={stateError}
                error={stateError !== ""}
              />

              <TextField
                label="Zip"
                type="text"
                value={zip}
                onChange={(e) => {
                  setZip(e.target.value);
                  if (zipError) setZipError("");
                }}
                fullWidth
                margin="normal"
                required
                helperText={zipError}
                error={zipError !== ""}
              />
              <div>
                <FormControl component="fieldset" margin="normal">
                  <FormLabel component="legend">
                    Message Preference{" "}
                    {showOptInStatus && (
                      hasOptedIn === true ? (
                        <span style={{ color: "green", fontWeight: "bold" }}>✓ Phone is Opted In</span>
                      ) : (
                        <span style={{ color: "red", fontWeight: "bold" }}>
                          {messagePreference !== "email" ? "* Phone is Not Opted in" : ""}
                        </span>
                      )
                    )}
                  </FormLabel>
                  <RadioGroup
                    value={messagePreference}
                    onChange={(e) => handleMessagePreferenceChange(e.target.value)}
                    row
                  >
                    <FormControlLabel value="email" control={<Radio />} label="Email" />
                    <FormControlLabel value="sms" control={<Radio />} label="SMS" />
                    <FormControlLabel value="both" control={<Radio />} label="Both" />
                  </RadioGroup>
                </FormControl>
                {updateStatus === "success" && (
                  <Typography style={{ color: "green", marginTop: "0.5rem" }}>
                    Profile updated successfully!
                  </Typography>
                )}
                {updateStatus === "error" && (
                  <Typography style={{ color: "red", marginTop: "0.5rem" }}>
                    Failed to update profile.
                  </Typography>
                )}
              </div>
              <div style={{ gap: "1rem", display: "flex", justifyContent: "left" }}>
                <Button variant="outlined" color="primary" style={{ marginTop: "1rem" }} onClick={profilePage}>
                  Back
                </Button>
                <Button type="submit" variant="outlined" color="primary" style={{ marginTop: "1rem" }}>
                  Update Profile
                </Button>
              </div>
            </form>
          </AccordionDetails>
        </Accordion>

        {/* Password Change Accordion */}
        <Accordion expanded={isPasswordOpen} onChange={() => setIsPasswordOpen(!isPasswordOpen)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Change Password</AccordionSummary>
          <AccordionDetails>
            <form onSubmit={handlePasswordChange} style={{ width: "100%" }}>
              <TextField
                label="Current Password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                margin="normal"
                helperText="Password must be at least 8 characters"
                required
              />
              <TextField
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
              />
              <Box mt={2}>
                {passwordUpdateStatus === "success" && (
                  <Typography style={{ color: "green" }}>Password updated successfully!</Typography>
                )}
                {passwordUpdateStatus === "error" && (
                  <Typography style={{ color: "red" }}>
                    {newPassword !== confirmPassword
                      ? "New passwords do not match"
                      : newPassword.length < 8
                        ? "Password must be at least 8 characters"
                        : "Failed to update password. Check current password."}
                  </Typography>
                )}
              </Box>
              <div style={{ gap: "1rem", display: "flex", justifyContent: "left" }}>
                <Button type="submit" variant="outlined" color="primary" style={{ marginTop: "1rem" }}>
                  Change Password
                </Button>
              </div>
            </form>
          </AccordionDetails>
        </Accordion>

        {/* Opt-In Popup Dialog */}
        <Dialog open={optInOpen} onClose={() => setOptInOpen(false)}>
          <DialogTitle><b>Message Opt-In Agreement</b></DialogTitle>
          <DialogContent>
            <Typography>
              To receive messages via SMS from Lunde Water LLC, you must explicitly agree to opt-in.
              Please confirm your preference.
              <br />
              <br />
              <em>Message & data rates may apply. Reply STOP to opt out.</em>
            </Typography>
            <FormControl component="fieldset" margin="normal">
              <RadioGroup
                value={optInSelection === null ? "" : optInSelection ? "yes" : "no"}
                onChange={(e) => setOptInSelection(e.target.value === "yes")}
              >
                <FormControlLabel value="yes" control={<Radio />} label="I agree to receive account updates, notifications, marketing messages and special offers via SMS from Lunde Water LLC." />
                <FormControlLabel value="no" control={<Radio />} label="I do not agree to receive account updates, notifications, marketing messages and special offers via SMS from Lunde Water LLC." />
              </RadioGroup>
            </FormControl>
            {optInSubmitStatus === "success" && (
              <Typography style={{ color: "green", marginTop: "0.5rem" }}>
                Opt-in preference saved successfully!
              </Typography>
            )}
            {optInSubmitStatus === "error" && (
              <Typography style={{ color: "red", marginTop: "0.5rem" }}>
                Failed to save opt-in preference. Please try again.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleOptInCancel} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleOptInConfirm} color="primary" disabled={optInSelection === null}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Container>
  );
};